import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { ReduxStates } from '../../redux/reduxTypes';
import * as DataActions from '../../redux/actions/data';
import { setMarketFilters } from '../../redux/actions/filters';
import { setSavedMarketFilters } from '../../redux/actions/saved';

import { Link, useLocation } from 'react-router-dom';
import { useOktaAuth } from '@okta/okta-react';

import Options from './component/Options';

import { User as UserInterface } from '../../types';
import ComingSoon from './component/coming-soon';

import { parseDefaultFilters } from '../../helpers/format';
import haleonLogo from '../../assets/logos/haleon-logo.png';
import threeMLogo from '../../assets/logos/3m-logo.png';
import demoLogo from '../../assets/logos/demo-logo.png';
import purinaLogo from '../../assets/logos/purina-logo.jpg';
import glanbiaLogo from '../../assets/logos/gpn-logo.png';

interface VariantProps {
  [key: string]: string;
}
interface OptionsProps {
  id: string;
  name: string;
  link: string;
  status: string;
  module: string;
}

const variantsStyles: VariantProps = {
  active: 'color-fontBlock border-b-2 border-fontBlock',
  default: 'border-b-[2px] border-transparent'
};

const options: Array<OptionsProps> = [
  {
    id: 'glance',
    name: 'glance_title',
    link: '/glance',
    status: 'enabled',
    module: 'At a Glance'
  },
  {
    id: 'overview',
    name: 'overview_title',
    link: '/overview',
    status: 'enabled',
    module: 'Overview'
  },
  {
    id: 'scratchpad',
    name: 'scratchpad_title',
    link: '/scratchpad',
    status: 'enabled',
    module: 'Scratchpad'
  },
  {
    id: 'notes',
    name: 'notes_title',
    link: '/notes',
    status: 'enabled',
    module: 'Notes: Read'
  }
];

const Navigation = (): React.ReactElement | null => {
  const dispatch = useDispatch();

  const { authState, oktaAuth } = useOktaAuth();
  const { pathname } = useLocation();

  const [showOptions, setShowOptions] = useState<boolean>(false);
  const [user, setUser] = useState<UserInterface | undefined>(undefined);
  const [logo, setLogo] = useState<string>('');
  const [helpHref, setHelpHref] = useState<string>('');
  const copyData = useSelector((state: ReduxStates) => state.copyData);
  const userInfo = useSelector((state: ReduxStates) => state.user);
  const filters = useSelector((state: ReduxStates) => state.filters);
  const metadata = useSelector((state: ReduxStates) => state.data.metaData);
  const newMsgsCount = useSelector((state: ReduxStates) => state.data.newMsgsCount);
  const [hasReadAccess, setHasReadAccess] = useState<boolean>(false);

  useEffect(() => {
    if (userInfo && userInfo.userModules) {
      setHasReadAccess(userInfo.userModules.includes('Notes: Read'));
    }
  }, [userInfo]);

  const renderUserInitials = (): string => {
    if (!user) return '';

    return `${user?.given_name?.charAt(0)}${user?.family_name?.charAt(0)}`;
  };

  const fetchUserCredentials = async () => {
    try {
      const user = await oktaAuth.getUser();
      setUser({ name: user.name, email: user.email, family_name: user.family_name, given_name: user.given_name });
    } catch (error) {
      console.log(error);
    }
  };

  useEffect(() => {
    if (authState?.isAuthenticated && logo === '' && metadata) {
      switch (metadata.shortName) {
        case 'haleon':
          setLogo(haleonLogo);
          break;
        case 'purina':
          setLogo(purinaLogo);
          break;
        case '3m':
          setLogo(threeMLogo);
          break;
        case 'demo':
          setLogo(demoLogo);
          break;
        case 'glanbia':
          setLogo(glanbiaLogo);
          break;
        default:
          setLogo(metadata.logoUrl);
      }
      // setLogo(metadata.logoUrl);
      setHelpHref(metadata.helpHref);
      dispatch(DataActions.setDefaultCurrency(metadata.defaultCurrency));
      dispatch(DataActions.setSelectedCurrency(metadata.defaultCurrency));
      dispatch(DataActions.setGlanceMarkets(metadata.ataGlanceMarkets));
      dispatch(DataActions.setGlanceDefaults(metadata.ataGlanceDefaults));

      //Only add default filters if there are no cookied filters
      if (!filters.market.length) {
        const parsedFilters = parseDefaultFilters(metadata.filterDefaults);
        dispatch(setMarketFilters(parsedFilters));
        dispatch(setSavedMarketFilters(parsedFilters));
      }
    }
  }, [authState, metadata]);

  useEffect(() => {
    if (authState?.isAuthenticated && !user) {
      fetchUserCredentials();
    }
  }, [authState]);

  useEffect(() => {
    const handleClicks = () => {
      if (showOptions) setShowOptions(false);
    };

    window.addEventListener('click', handleClicks);

    return () => {
      window.removeEventListener('click', handleClicks);
    };
  }, [showOptions]);

  if (!authState?.isAuthenticated) return null;

  const linkStyles =
    'text-[14px] font-medium tracking-[0.1px] no-underline border-transparent font-nunitoMedium last:mr-0';
  const enabledStyles = `${linkStyles} text-fontBlock`;
  const disabledStyles = ` ${linkStyles} relative pointer-events-none text-borderGray cursor-default `;
  const disabledHover = 'absolute w-full h-full group z-50';

  const returnStatueClasses = (option: OptionsProps) => {
    if (userInfo && userInfo.userModules) {
      const { status, module } = option;
      const disabled = status.toLocaleLowerCase() === 'disabled' || !userInfo.userModules.includes(module);
      return disabled ? disabledStyles : enabledStyles;
    }
  };

  return (
    <nav className="flex items-center bg-white justify-between py-0 px-5 h-[56px] shadow-navShadow">
      <div className="flex">
        {logo !== '' && (
          <div className="w-32 h-12 p-0.5">
            <img src={logo} alt="company logo" className="h-full" />
          </div>
        )}
        <div className="flex items-center space-x-10 ml-10 pt-1">
          {copyData &&
            userInfo.userModules &&
            options.map((option, idx) => (
              <div
                key={idx}
                id={`navigation_button_${option.id}`}
                className={`
                  group relative flex items-center h-[120%]
                  ${pathname.includes(option.link) ? variantsStyles.active : variantsStyles.default}
                  `}
              >
                {option.status === 'disabled' &&
                  userInfo &&
                  userInfo.userModules &&
                  userInfo.userModules.includes(option.module) && (
                    <div className={disabledHover}>
                      <ComingSoon />
                    </div>
                  )}
                <Link className={returnStatueClasses(option)} to={option.link}>
                  {copyData[option.name]}{' '}
                  {newMsgsCount > 0 && option.name === 'notes_title' && hasReadAccess ? (
                    <sup className="text-[12px] px-1 rounded-full bg-navy text-white">{newMsgsCount}</sup>
                  ) : (
                    ''
                  )}
                </Link>
              </div>
            ))}
        </div>
      </div>
      <div className="relative">
        {showOptions && <Options user={user} helpHref={helpHref} />}
        <div
          className="cursor-pointer h-[30px] w-[30px] rounded-full bg-navy flex justify-center items-center"
          onClick={() => setShowOptions(!showOptions)}
        >
          <span className="text-white font-nunitoBold text-xs">{renderUserInitials()}</span>
        </div>
        {/* <Avatar onClick={() => setShowOptions(!showOptions)} /> */}
      </div>
    </nav>
  );
};

export default Navigation;
